import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDialog, Pagination } from '../../components';
import {
  staffActivitiesService,
  staffVolunteersService,
  staffVolunteerTeamsService,
  STAFF_PAGE_SIZE,
} from '../../lib/api-service';
import { apiFetch, getToken, BACKEND_URL } from '../../lib/api';
import type {
  StaffActivity,
  PaginatedResponse,
  VolunteerApplication,
  VolunteerTeam,
} from '../../lib/types';
import { formatDate } from '../../utils/date';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  MessageSquareText,
  Printer,
  Search,
  Undo2,
  Users,
  XCircle,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/tooltip';
import { HelpButton } from '../../components/HelpButton';

const STATUS_LABELS: Record<string, { label: string; cls: string; icon: typeof CheckCircle2 }> = {
  pending: { label: 'În așteptare', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Aprobat', cls: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Respins', cls: 'bg-red-100 text-red-600', icon: XCircle },
  withdrawn: { label: 'Retras', cls: 'bg-gray-100 text-gray-600', icon: XCircle },
};

export default function ActivityVolunteers() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [activity, setActivity] = useState<StaffActivity | null>(null);
  const [volunteers, setVolunteers] = useState<VolunteerApplication[]>([]);
  const [teams, setTeams] = useState<VolunteerTeam[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Review dialog
  const [reviewTarget, setReviewTarget] = useState<VolunteerApplication | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'rescind' | 'revert_to_pending' | 'revert_to_approved'>('approve');

  // Notes dialog
  const [notesTarget, setNotesTarget] = useState<VolunteerApplication | null>(null);
  const [notesValue, setNotesValue] = useState('');

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const [activityData, volData, teamData] = await Promise.all([
        staffActivitiesService.detail(slug!),
        staffVolunteersService.list(slug, {
          page,
          status: statusFilter || undefined,
          team: teamFilter || undefined,
          search: search || undefined,
        }),
        staffVolunteerTeamsService.list(slug),
      ]);
      setActivity(activityData);
      setVolunteers(volData.results);
      setTotalCount(volData.count);
      setTeams(teamData.results);
    } catch {
      setError('Nu s-au putut încărca datele.');
    } finally {
      setLoading(false);
    }
  }, [slug, page, statusFilter, teamFilter, search]);

  useEffect(() => { load(); }, [load]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  async function handleTeamChange(v: VolunteerApplication, teamId: number | null) {
    if (!slug) return;
    try {
      await staffVolunteersService.update(slug, v.id, { team: teamId });
      await load();
    } catch {
      setError('Eroare la schimbarea echipei.');
    }
  }

  async function confirmReview() {
    if (!reviewTarget || !slug) return;
    try {
      await staffVolunteersService.review(slug, reviewTarget.id, { action: reviewAction });
      setReviewTarget(null);
      await load();
    } catch {
      setError('Eroare la procesarea cererii.');
    }
  }

  async function saveNotes() {
    if (!notesTarget || !slug) return;
    try {
      await staffVolunteersService.update(slug, notesTarget.id, { staff_notes: notesValue });
      setNotesTarget(null);
      await load();
    } catch {
      setError('Eroare la salvarea notelor.');
    }
  }

  function buildFilterParams() {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (teamFilter) params.set('team', teamFilter);
    if (search) params.set('search', search);
    return params.toString();
  }

  async function exportCSV() {
    if (!activity || !slug) return;
    const filterQs = buildFilterParams();
    const allData = await apiFetch<PaginatedResponse<VolunteerApplication>>(
      `/staff/activities/${slug}/volunteers/?page_size=10000${filterQs ? '&' + filterQs : ''}`
    );
    const all = allData.results;
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const headers = ['Nume', 'Prenume', 'Email', 'Echipa', 'Status', 'Motivație', 'Data aplicării'];
    const rows = all.map((v) => [
      escape(v.user_last_name),
      escape(v.user_first_name),
      escape(v.user_email),
      escape(v.team_name || '—'),
      escape(STATUS_LABELS[v.status]?.label || v.status),
      escape(v.motivation || ''),
      escape(formatDate(v.created_at)),
    ]);
    const csv = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = formatDate(new Date().toISOString()).replace(/\//g, '-');
    link.href = blobUrl;
    link.download = `voluntari_${activity.slug}_${today}.csv`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  }

  async function handlePrint() {
    if (!slug) return;
    try {
      const token = getToken();
      const filterQs = buildFilterParams();
      const res = await fetch(`${BACKEND_URL}/api/v1/staff/activities/${slug}/volunteers/?output=print${filterQs ? '&' + filterQs : ''}`, {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed');
      const html = await res.text();
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
    } catch {
      setError('Eroare la generarea listei de printare.');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const approved = volunteers.filter((v) => v.status === 'approved');
  const pending = volunteers.filter((v) => v.status === 'pending');

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/staff/activitati')}
          className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              Voluntari
            </h1>
            <HelpButton to="/staff/documentatie/voluntari" />
          </div>
          {activity && (
            <p className="mt-0.5 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              {activity.title}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            disabled={volunteers.length === 0}
            className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Printează lista"
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Printează</span>
          </button>
          <button
            onClick={exportCSV}
            disabled={volunteers.length === 0}
            className="flex items-center gap-2 rounded-lg border border-[var(--vivid-purple)] px-3 py-2 text-sm font-semibold text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/5 disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Stats */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5">
          <Users className="h-4 w-4 text-[var(--vivid-purple)]" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            {totalCount} total
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-semibold text-green-700" style={{ fontFamily: 'var(--font-primary)' }}>
            {approved.length} aprobați
          </span>
        </div>
        {pending.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2.5">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700" style={{ fontFamily: 'var(--font-primary)' }}>
              {pending.length} în așteptare
            </span>
          </div>
        )}
        {activity?.volunteer_capacity && (
          <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-2.5">
            <span className="text-sm font-semibold text-blue-700" style={{ fontFamily: 'var(--font-primary)' }}>
              {approved.length} / {activity.volunteer_capacity} locuri
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Caută după nume sau email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border bg-white py-2 pl-10 pr-4 text-sm"
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border bg-white px-3 py-2 text-sm"
          style={{ fontFamily: 'var(--font-secondary)' }}
        >
          <option value="">Toate statusurile</option>
          <option value="pending">În așteptare</option>
          <option value="approved">Aprobat</option>
          <option value="rejected">Respins</option>
          <option value="withdrawn">Retras</option>
        </select>
        {teams.length > 0 && (
          <select
            value={teamFilter}
            onChange={(e) => { setTeamFilter(e.target.value); setPage(1); }}
            className="rounded-lg border bg-white px-3 py-2 text-sm"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <option value="">Toate echipele</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      {volunteers.length === 0 ? (
        <div className="rounded-xl border bg-white py-16 text-center shadow-sm">
          <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Nu există voluntari pentru această activitate.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Nume</th>
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Email</th>
                  <th className="hidden px-4 py-3 text-left font-semibold md:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Echipa</th>
                  <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Status</th>
                  <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Data</th>
                  <th className="px-4 py-3 text-right font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Acțiuni</th>
                </tr>
              </thead>
              <tbody style={{ fontFamily: 'var(--font-secondary)' }}>
                {volunteers.map((v) => {
                  const statusInfo = STATUS_LABELS[v.status] || STATUS_LABELS.pending;
                  const StatusIcon = statusInfo.icon;
                  return (
                    <tr key={v.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {v.user_first_name} {v.user_last_name}
                          {(v.motivation || v.user_volunteer_bio) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" className="text-muted-foreground hover:text-[var(--vivid-purple)]">
                                  <MessageSquareText className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs whitespace-pre-wrap bg-white text-foreground border shadow-lg p-3">
                                {v.user_volunteer_bio && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium mb-0.5" style={{ fontFamily: 'var(--font-primary)' }}>Bio voluntar:</p>
                                    <p className="text-xs" style={{ fontFamily: 'var(--font-secondary)' }}>{v.user_volunteer_bio}</p>
                                  </div>
                                )}
                                {v.motivation && (
                                  <div>
                                    <p className="text-xs font-medium mb-0.5" style={{ fontFamily: 'var(--font-primary)' }}>Motivație:</p>
                                    <p className="text-xs" style={{ fontFamily: 'var(--font-secondary)' }}>{v.motivation}</p>
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{v.user_email}</td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        {teams.length > 0 ? (
                          <select
                            value={v.team ?? ''}
                            onChange={(e) => handleTeamChange(v, e.target.value ? parseInt(e.target.value) : null)}
                            className="rounded border bg-transparent px-1.5 py-1 text-xs text-muted-foreground"
                          >
                            <option value="">Fără echipă</option>
                            {teams.map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-muted-foreground">{v.team_name || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${statusInfo.cls}`}>
                          <StatusIcon className="h-3 w-3" /> {statusInfo.label}
                        </span>
                      </td>
                      <td className="hidden whitespace-nowrap px-4 py-3 text-muted-foreground sm:table-cell">
                        {formatDate(v.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {v.status === 'pending' && (
                            <>
                              <button
                                onClick={() => { setReviewTarget(v); setReviewAction('approve'); }}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-green-600 transition-colors hover:bg-green-50"
                              >
                                Aprobă
                              </button>
                              <button
                                onClick={() => { setReviewTarget(v); setReviewAction('reject'); }}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                              >
                                Respinge
                              </button>
                            </>
                          )}
                          {v.status === 'approved' && (
                            <button
                              onClick={() => { setReviewTarget(v); setReviewAction('rescind'); }}
                              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-amber-600 transition-colors hover:bg-amber-50"
                            >
                              <Undo2 className="h-3 w-3" /> Retrage
                            </button>
                          )}
                          {(v.status === 'rejected' || v.status === 'withdrawn') && (
                            <>
                              <button
                                onClick={() => { setReviewTarget(v); setReviewAction('revert_to_pending'); }}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-amber-600 transition-colors hover:bg-amber-50"
                              >
                                → Așteptare
                              </button>
                              <button
                                onClick={() => { setReviewTarget(v); setReviewAction('revert_to_approved'); }}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-green-600 transition-colors hover:bg-green-50"
                              >
                                → Aprobat
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => { setNotesTarget(v); setNotesValue(v.staff_notes); }}
                            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/5"
                          >
                            Note
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      {/* Review confirmation dialog */}
      <ConfirmDialog
        open={!!reviewTarget}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        onConfirm={confirmReview}
        title={
          ({ approve: 'Aprobă voluntarul', reject: 'Respinge voluntarul', rescind: 'Retrage aprobarea', revert_to_pending: 'Revenire la „În așteptare"', revert_to_approved: 'Revenire la „Aprobat"' } as Record<string, string>)[reviewAction] || ''
        }
        confirmLabel={
          ({ approve: 'Aprobă', reject: 'Respinge', rescind: 'Retrage', revert_to_pending: 'Confirmă', revert_to_approved: 'Confirmă' } as Record<string, string>)[reviewAction] || ''
        }
        variant={['approve', 'revert_to_approved', 'revert_to_pending'].includes(reviewAction) ? 'confirm' : 'danger'}
        description={
          `${reviewTarget?.user_first_name} ${reviewTarget?.user_last_name}: ${
            ({ approve: 'Confirmi aprobarea ca voluntar?', reject: 'Sigur vrei să respingi cererea?', rescind: 'Sigur vrei să retragi aprobarea? Voluntarul va fi notificat.', revert_to_pending: 'Mută voluntarul înapoi la „În așteptare"?', revert_to_approved: 'Reaprobă voluntarul? Va fi notificat prin email.' } as Record<string, string>)[reviewAction] || ''
          }`
        }
      />

      {/* Staff notes dialog */}
      {notesTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-3 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              Note interne — {notesTarget.user_first_name} {notesTarget.user_last_name}
            </h3>
            <textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              rows={4}
              className="mb-4 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ fontFamily: 'var(--font-secondary)' }}
              placeholder="Adaugă note vizibile doar pentru staff..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setNotesTarget(null)}
                className="rounded-lg border px-4 py-2 text-sm font-medium"
              >
                Anulează
              </button>
              <button
                onClick={saveNotes}
                className="rounded-lg bg-[var(--vivid-purple)] px-4 py-2 text-sm font-semibold text-white"
              >
                Salvează
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
